import React, { forwardRef } from 'react';
import { cn } from '../lib/utils/cn';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ComponentType<{ className?: string }>;
  rightIcon?: React.ComponentType<{ className?: string }>;
  variant?: 'default' | 'filled' | 'outlined';
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  variant = 'default',
  className,
  id,
  ...props
}, ref) => {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

  const baseClasses = 'w-full px-3 py-2 text-sm transition-colors placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50';
  
  const variants = {
    default: 'border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500',
    filled: 'border-0 rounded-lg bg-gray-100 focus:bg-white focus:ring-2 focus:ring-brand-500',
    outlined: 'border-2 border-gray-200 rounded-lg bg-transparent focus:border-brand-500'
  };

  const errorClasses = error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '';

  const inputClasses = cn(
    baseClasses,
    variants[variant],
    errorClasses,
    LeftIcon && 'pl-10',
    RightIcon && 'pr-10',
    className
  );

  return (
    <div className="space-y-1">
      {label && (
        <label 
          htmlFor={inputId}
          className={cn(
            'block text-sm font-medium',
            error ? 'text-red-700' : 'text-gray-700'
          )}
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        {LeftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <LeftIcon className={cn(
              'h-4 w-4',
              error ? 'text-red-500' : 'text-gray-400'
            )} />
          </div>
        )}
        
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          {...props}
        />
        
        {RightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <RightIcon className={cn(
              'h-4 w-4',
              error ? 'text-red-500' : 'text-gray-400'
            )} />
          </div>
        )}
      </div>
      
      {(error || helperText) && (
        <p className={cn(
          'text-xs',
          error ? 'text-red-600' : 'text-gray-500'
        )}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  variant?: 'default' | 'filled' | 'outlined';
  children: React.ReactNode;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  label,
  error,
  helperText,
  variant = 'default',
  className,
  id,
  children,
  ...props
}, ref) => {
  const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');

  const baseClasses = 'w-full px-3 py-2 text-sm transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 bg-white';
  
  const variants = {
    default: 'border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500',
    filled: 'border-0 rounded-lg bg-gray-100 focus:bg-white focus:ring-2 focus:ring-brand-500',
    outlined: 'border-2 border-gray-200 rounded-lg bg-transparent focus:border-brand-500'
  };

  const errorClasses = error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '';

  const selectClasses = cn(
    baseClasses,
    variants[variant],
    errorClasses,
    className
  );

  return (
    <div className="space-y-1">
      {label && (
        <label 
          htmlFor={selectId}
          className={cn(
            'block text-sm font-medium',
            error ? 'text-red-700' : 'text-gray-700'
          )}
        >
          {label}
        </label>
      )}
      
      <select
        ref={ref}
        id={selectId}
        className={selectClasses}
        {...props}
      >
        {children}
      </select>
      
      {(error || helperText) && (
        <p className={cn(
          'text-xs',
          error ? 'text-red-600' : 'text-gray-500'
        )}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

export { Input, Select };